/**
 * snoozeDate — Walter 2026-08-24. THE one snooze rule, shared by every
 * surface (agenda row, edit popover, overdue triage, email edit page):
 *
 *   snooze N days = push out N days from whichever is LATER,
 *                   today or the reminder's own date,
 *                   at the reminder's ORIGINAL time-of-day.
 *
 * So an overdue or due-today reminder goes to today+N — the thing you keep
 * seeing and keep deferring lands tomorrow, not on some date in the past
 * plus a day. A reminder already set for a FUTURE day moves N days further
 * out from where it was: snoozing something set for Sept 6 gives Sept 7.
 *
 * HISTORY, because this has now flipped twice. The surfaces originally had
 * three different behaviours. On 2026-07-19, after a trip where snoozing
 * overdue items did nothing useful, Walter asked for one uniform rule —
 * "snooze should always snooze to tomorrow (today + 1), not original date
 * + 1" — and future reminders got dragged back to tomorrow along with the
 * overdue ones. On 2026-08-24 he snoozed a Sept 6 reminder, got Aug 26, and
 * said: "I think that's what I actually had in mind at the time, but I
 * wasn't clear." Hence the split — but ONE rule with a condition in it, not
 * one rule per surface, which is what caused the original mess.
 *
 * The time-of-day preservation is the 2026-05-18 lesson (snooze means
 * "later, same time", never a surprise 7am). Undated reminders have no date
 * to push from, so they keep the current time-of-day.
 */

export function snoozeToDays(originalDate, days = 1, now = new Date()) {
  const orig = originalDate instanceof Date && !isNaN(originalDate.getTime())
    ? originalDate
    : null;
  // Push from the reminder's own day when that day is still ahead of us;
  // otherwise from today. Compared by CALENDAR DAY, so a reminder due later
  // today counts as today (today+N and original+N agree there anyway).
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const anchor = (orig && startOfDay(orig) > startOfDay(now)) ? orig : now;
  const next = new Date(anchor);
  next.setDate(anchor.getDate() + days);
  if (orig) next.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
  else next.setSeconds(0, 0);
  return next;
}

// One toast label for every snooze surface — "Mon, Jul 21, 10:00 AM".
export function formatSnoozeLabel(d) {
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// One toast TTL for every reminder-action toast (Walter 2026-07-19: "so
// quick I barely can see it" — and inconsistent across views).
export const REMINDER_TOAST_TTL_MS = 6000;
